import { useState } from "react";
import { Link } from "react-router-dom";
import { X, ExternalLink, Barcode, Sparkles, Mail, MailWarning, ShieldCheck, Share2 } from "lucide-react";

import { formatDate, formatCurrency, truncHash } from "@/lib/api";
import ShareCertificateDialog from "@/components/ShareCertificateDialog";

const Field = ({ label, children, mono, testid }) => (
  <div className="py-2.5 grid grid-cols-3 gap-3 border-b border-gray-100 last:border-0">
    <div className="text-[11px] font-semibold tracking-wider uppercase text-gray-500">
      {label}
    </div>
    <div
      data-testid={testid}
      className={`col-span-2 text-sm text-gray-900 ${mono ? "font-mono break-all" : ""}`}
    >
      {children || <span className="text-gray-400">—</span>}
    </div>
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    minted: "bg-blue-50 text-blue-700 border-blue-200",
    pending_data: "bg-amber-50 text-amber-800 border-amber-200",
    pending_fulfillment: "bg-amber-50 text-amber-800 border-amber-200",
    ready_to_mint: "bg-emerald-50 text-emerald-800 border-emerald-200",
    minting: "bg-indigo-50 text-indigo-800 border-indigo-200",
    mint_failed: "bg-red-50 text-red-700 border-red-200",
    revoked: "bg-red-50 text-red-700 border-red-200",
  };
  const cls = map[status] || "bg-gray-100 text-gray-700 border-gray-200";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      {status?.replace(/_/g, " ") || "—"}
    </span>
  );
};

export default function RecordDetailDrawer({ open, receipt, onClose, onFulfill, onResendEmail }) {
  const [shareOpen, setShareOpen] = useState(false);
  if (!open || !receipt) return null;
  const r = receipt;
  const item = r.items?.[0];
  const isMinted = r.status === "minted";
  const isPending = ["pending_data", "pending_fulfillment", "ready_to_mint", "mint_failed"].includes(r.status);

  const POLY = "https://amoy.polygonscan.com";
  const hex = (h) => (!h ? null : h.startsWith("0x") ? h : `0x${h}`);

  return (
    <div
      data-testid="record-drawer"
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-gray-900/40 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white shadow-xl border-l border-gray-200 flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <div className="font-display font-semibold text-gray-900">{item?.name || "Record"}</div>
            <div className="text-[11px] tracking-wider uppercase text-gray-500 font-mono">
              {r.receipt_id}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="record-drawer-close"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-gray-500">
                Status
              </div>
              <StatusPill status={r.status} />
            </div>
            {r.mint_error && (
              <div className="mt-2 rounded-md bg-red-50 border border-red-200 p-2.5 text-xs text-red-800">
                {r.mint_error}
              </div>
            )}
          </section>

          <section>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-gray-500 mb-2">
              Order
            </div>
            <Field label="Receipt ID" mono>{r.receipt_id}</Field>
            <Field label="Order ID" mono>{r.source_order_id}</Field>
            <Field label="Source">{r.source}</Field>
            <Field label="Line Item ID" mono>{r.source_line_item_id}</Field>
            <Field label="Unit">{r.source_unit_index ?? "—"}</Field>
            <Field label="Item">{item?.name}</Field>
            <Field label="SKU" mono>{item?.sku}</Field>
            <Field label="Customer">{r.customer_email}</Field>
            <Field label="Amount">{formatCurrency(r.total, r.currency)}</Field>
            <Field label="Created">{formatDate(r.created_at || r.purchase_date)}</Field>
          </section>

          <section>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-gray-500 mb-2">
              Issuance
            </div>
            <Field label="Serial Number" mono>{r.serial_number}</Field>
            <Field label="Certificate ID" mono>{r.token_id}</Field>
            <Field label="Verification Transaction" mono testid="drawer-tx-hash">
              {r.tx_hash ? (
                <a
                  href={`${POLY}/tx/${hex(r.tx_hash)}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-[#0055FF] hover:underline"
                >
                  {truncHash(hex(r.tx_hash), 10, 8)}
                  <ExternalLink size={11} />
                </a>
              ) : null}
            </Field>
            <Field label="Contract" mono>
              {r.contract_address ? (
                <a
                  href={`${POLY}/address/${hex(r.contract_address)}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-[#0055FF] hover:underline"
                >
                  {truncHash(hex(r.contract_address), 8, 6)}
                  <ExternalLink size={11} />
                </a>
              ) : null}
            </Field>
            <Field label="Network">{r.contract_address ? "Polygon Amoy" : "—"}</Field>
            <Field label="Issued At">{r.minted_at ? formatDate(r.minted_at) : "—"}</Field>
            <Field label="Invalidated At">{r.revoked_at ? formatDate(r.revoked_at) : "—"}</Field>
            <Field label="Invalidation Tx" mono>
              {r.revoke_tx_hash ? (
                <a
                  href={`${POLY}/tx/${hex(r.revoke_tx_hash)}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-[#0055FF] hover:underline"
                >
                  {truncHash(hex(r.revoke_tx_hash), 10, 8)}
                  <ExternalLink size={11} />
                </a>
              ) : null}
            </Field>
          </section>

          <section>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-gray-500 mb-2">
              Email
            </div>
            <Field label="Sent">
              {r.certificate_email_sent === true ? (
                <span className="inline-flex items-center gap-1.5 text-emerald-700">
                  <Mail size={12} /> Yes
                </span>
              ) : r.certificate_email_error ? (
                <span className="inline-flex items-center gap-1.5 text-red-700">
                  <MailWarning size={12} /> Failed
                </span>
              ) : (
                "—"
              )}
            </Field>
            {r.certificate_email_error && (
              <Field label="Error">{r.certificate_email_error}</Field>
            )}
            {r.certificate_email_sent_at && (
              <Field label="Sent At">{new Date(r.certificate_email_sent_at).toLocaleString()}</Field>
            )}
          </section>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/60 flex items-center justify-end gap-2 flex-wrap">
          {isPending && (
            <button
              onClick={() => onFulfill?.(r)}
              data-testid="drawer-fulfill-btn"
              className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#0044CC]"
            >
              <Barcode size={14} />
              {r.status === "ready_to_mint" ? "Issue Certificate" : r.status === "mint_failed" ? "Retry Issuance" : "Complete"}
            </button>
          )}
          {isMinted && (
            <Link
              to={`/verify/${r.nft_id}`}
              data-testid="drawer-view-cert"
              className="inline-flex items-center gap-1.5 bg-white text-gray-900 border border-gray-200 text-sm font-semibold px-4 py-2 rounded-md hover:border-[#0055FF] hover:text-[#0055FF]"
            >
              <ShieldCheck size={14} />
              View Certificate
            </Link>
          )}
          {isMinted && r.tx_hash && (
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              data-testid="drawer-share-btn"
              className="inline-flex items-center gap-1.5 bg-white text-gray-700 border border-gray-200 text-sm font-semibold px-4 py-2 rounded-md hover:border-[#0055FF] hover:text-[#0055FF]"
            >
              <Share2 size={14} />
              Share Certificate
            </button>
          )}
          {(r.customer_email || "").trim() && (
            <button
              onClick={() => onResendEmail?.(r)}
              data-testid="drawer-resend-email"
              className="inline-flex items-center gap-1.5 bg-white text-gray-700 border border-gray-200 text-sm font-semibold px-4 py-2 rounded-md hover:border-[#0055FF] hover:text-[#0055FF]"
            >
              <Sparkles size={14} />
              Resend Email
            </button>
          )}
        </div>
      </div>
      <ShareCertificateDialog
        open={shareOpen}
        txHash={r.tx_hash}
        itemName={item?.name}
        onClose={() => setShareOpen(false)}
      />
    </div>
  );
}
